import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertTriangle, Clock, Navigation, Ambulance as AmbulanceIcon } from 'lucide-react';
import AppHeader from '../../../components/AppHeader';
import BottomNav from '../../../components/BottomNav';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'https://drvivara.cloud/api' });
api.interceptors.request.use(c => { const t = localStorage.getItem('token') || sessionStorage.getItem('token'); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });

const EMERGENCY_TYPES = [
  { id: 'accident', label: 'Accident', icon: '🚗', color: 'bg-red-900/30 border-yellow-400' },
  { id: 'cardiac', label: 'Cardiac', icon: '💓', color: 'bg-rose-900/30 border-rose-500' },
  { id: 'respiratory', label: 'Respiratory', icon: '🫁', color: 'bg-blue-900/30 border-blue-500' },
  { id: 'pregnancy', label: 'Pregnancy', icon: '🤱', color: 'bg-pink-900/30 border-pink-500' },
  { id: 'injury', label: 'Injury', icon: '🩹', color: 'bg-orange-900/30 border-orange-500' },
  { id: 'poisoning', label: 'Poisoning', icon: '☠️', color: 'bg-purple-900/30 border-purple-500' },
  { id: 'burn', label: 'Burn', icon: '🔥', color: 'bg-amber-900/30 border-amber-500' },
  { id: 'stroke', label: 'Stroke', icon: '🧠', color: 'bg-indigo-900/30 border-indigo-500' },
  { id: 'other', label: 'Other', icon: '❓', color: 'bg-gray-700 border-gray-500' },
];

const STATUS_STYLES = {
  pending: 'bg-yellow-900/50 text-yellow-300',
  dispatched: 'bg-blue-900/50 text-blue-300',
  assigned: 'bg-purple-900/50 text-purple-300',
  en_route: 'bg-cyan-900/50 text-cyan-300',
  arrived: 'bg-green-900/50 text-green-300',
  transporting: 'bg-indigo-900/50 text-indigo-300',
  completed: 'bg-gray-700 text-gray-300',
  cancelled: 'bg-red-900/50 text-red-300',
};

const EmergencyRequest = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [step, setStep] = useState('form');
  const [type, setType] = useState('');
  const [description, setDescription] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [address, setAddress] = useState('');
  const [requesterName, setRequesterName] = useState('');
  const [phone, setPhone] = useState('');
  const [gpsLoading, setGpsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [myRequests, setMyRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);

  useEffect(() => {
    setRequesterName(user?.name || '');
    setPhone(user?.phone || '');
  }, [user]);

  const fetchMyRequests = async () => {
    setLoadingRequests(true);
    try {
      const res = await api.get('/emergency/my');
      setMyRequests(res.data.data || []);
    } catch (err) {
      showError('Failed to load requests');
    } finally {
      setLoadingRequests(false);
    }
  };

  useEffect(() => {
    if (step === 'my') fetchMyRequests();
  }, [step]);

  const detectGPS = () => {
    if (!navigator.geolocation) { showError('GPS not available'); return; }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(pos.coords.latitude.toFixed(6));
        setLng(pos.coords.longitude.toFixed(6));
        setGpsLoading(false);
        showSuccess('Location detected');
      },
      () => { setGpsLoading(false); showError('GPS failed, enter manually'); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSubmit = async () => {
    if (!type) { showError('Select emergency type'); return; }
    if (!requesterName.trim()) { showError('Enter your name'); return; }
    if (!phone.trim()) { showError('Enter your phone'); return; }
    if (!lat || !lng) { showError('Enable GPS or enter location'); return; }

    setSubmitting(true);
    try {
      const res = await api.post('/emergency/request', {
        emergencyType: type, description, location: { coordinates: [parseFloat(lng), parseFloat(lat)] },
        address, requesterName: requesterName.trim(), phone: phone.trim(),
      });
      setRequestId(res.data.data?.request?._id || res.data.data?._id);
      showSuccess('Emergency request submitted!');
      setStep('success');
    } catch (err) {
      showError(err.response?.data?.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setType(''); setDescription(''); setAddress(''); setRequestId(null);
    setStep('form');
  };

  return (
    <div className="bg-dark min-h-screen flex flex-col">
      <AppHeader title="Emergency Request" />
      <div className="flex-1 p-4 pb-24 overflow-y-auto">
        <div className="flex gap-2 mb-4">
          <button onClick={() => setStep('form')} className={`px-4 py-2 rounded-lg text-sm font-medium flex-1 text-center ${step === 'form' ? 'bg-gold text-dark' : 'bg-gray-800 text-gray-400'}`}><AlertTriangle size={16} className="inline mr-1" />Request</button>
          <button onClick={() => setStep('my')} className={`px-4 py-2 rounded-lg text-sm font-medium flex-1 text-center ${step === 'my' ? 'bg-gold text-dark' : 'bg-gray-800 text-gray-400'}`}><Clock size={16} className="inline mr-1" />My Requests</button>
        </div>

        {step === 'success' ? (
          <div className="bg-gray-800 rounded-2xl p-6 border border-green-500/30 text-center">
            <div className="text-5xl mb-4">✅</div>
            <h2 className="text-xl font-bold text-green-400 mb-2">Request Submitted</h2>
            <p className="text-gray-400 mb-4">Your emergency request has been received. Help is on the way.</p>
            {requestId && <p className="text-xs text-gray-500 mb-6">Request ID: {requestId}</p>}
            <button onClick={resetForm} className="bg-gold text-dark px-6 py-3 rounded-xl font-bold w-full">New Request</button>
          </div>
        ) : step === 'my' ? (
          <div>
            {loadingRequests ? (
              <p className="text-center text-gray-500 py-12">Loading...</p>
            ) : myRequests.length > 0 ? (
              <div className="space-y-3">
                {myRequests.map(req => (
                  <div key={req._id} className="bg-gray-800 rounded-xl p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="text-lg">{EMERGENCY_TYPES.find(t => t.id === req.emergencyType)?.icon} </span>
                        <span className="text-white font-medium capitalize">{req.emergencyType}</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded font-medium ${STATUS_STYLES[req.status] || 'bg-gray-700 text-gray-400'}`}>{req.status}</span>
                    </div>
                    {req.description && <p className="text-xs text-gray-400 mt-2">{req.description}</p>}
                    <p className="text-xs text-gray-500 mt-2">{new Date(req.createdAt).toLocaleString()}</p>
                    {req.driver?.name && <p className="text-xs text-gold mt-1">Driver: {req.driver.name} {req.driver.phone ? `(${req.driver.phone})` : ''}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-12">
                <AmbulanceIcon size={48} className="mx-auto mb-3 opacity-30" />
                <p>No emergency requests yet</p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-red-900/20 border border-yellow-400/30 rounded-xl p-4 mb-2">
              <p className="text-yellow-400 text-sm font-medium flex items-center gap-2"><AlertTriangle size={18} /> This is for medical emergencies. If life-threatening, call 911 immediately.</p>
            </div>

            <div>
              <label className="text-xs text-gray-400 mb-2 block font-medium">Emergency Type</label>
              <div className="grid grid-cols-3 gap-2">
                {EMERGENCY_TYPES.map(et => (
                  <button key={et.id} onClick={() => setType(et.id)} className={`p-3 rounded-xl border text-center transition ${type === et.id ? `${et.color} ring-2 ring-gold` : 'bg-gray-800 border-gray-700'}`}>
                    <div className="text-2xl mb-1">{et.icon}</div>
                    <div className="text-xs text-white">{et.label}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-400 mb-1 block font-medium">Description</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} className="w-full bg-gray-800 text-white rounded-xl p-3 text-sm focus:border-gold outline-none min-h-[80px]" placeholder="Describe the emergency..." />
            </div>

            <div>
              <label className="text-xs text-gray-400 mb-2 block font-medium">Your Location</label>
              <button onClick={detectGPS} disabled={gpsLoading} className="w-full bg-blue-900/30 text-blue-300 border border-blue-500/30 rounded-xl p-3 text-sm font-medium flex items-center justify-center gap-2 mb-2 disabled:opacity-50">
                <Navigation size={16} /> {gpsLoading ? 'Detecting...' : 'Auto-Detect GPS'}
              </button>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <input value={lat} onChange={e => setLat(e.target.value)} className="bg-gray-800 text-white rounded-xl p-3 text-sm focus:border-gold outline-none" placeholder="Latitude" />
                <input value={lng} onChange={e => setLng(e.target.value)} className="bg-gray-800 text-white rounded-xl p-3 text-sm focus:border-gold outline-none" placeholder="Longitude" />
              </div>
              <input value={address} onChange={e => setAddress(e.target.value)} className="w-full bg-gray-800 text-white rounded-xl p-3 text-sm focus:border-gold outline-none" placeholder="Address (optional)" />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-400 mb-1 block font-medium">Your Name</label>
                <input value={requesterName} onChange={e => setRequesterName(e.target.value)} className="w-full bg-gray-800 text-white rounded-xl p-3 text-sm focus:border-gold outline-none" />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block font-medium">Phone</label>
                <input value={phone} onChange={e => setPhone(e.target.value)} className="w-full bg-gray-800 text-white rounded-xl p-3 text-sm focus:border-gold outline-none" type="tel" />
              </div>
            </div>

            <button onClick={handleSubmit} disabled={submitting} className="w-full bg-yellow-500 text-white py-4 rounded-xl text-lg font-bold flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-amber-600 transition">
              {submitting ? 'Submitting...' : <><AmbulanceIcon size={24} /> Submit Emergency</>}
            </button>
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
};

export default EmergencyRequest;
